/**
 * @fileoverview Utility to calculate field-level differences between document states.
 * @module audit/changes
 *
 * Responsibilities:
 * - Compare before/after document snapshots
 * - Produce ChangeDetail array for audit entries
 * - Filter system/sensitive fields (_id, updatedAt, password, etc.)
 *
 * Dependencies:
 * - ./types (ChangeDetail)
 *
 * Architecture:
 * - Pure utility. No I/O. No side effects.
 *
 * Notes:
 * - Nested objects are compared by JSON stringification
 * - System/sensitive fields (_id, updatedAt, createdAt, password, etc.) are excluded via SKIP_FIELDS
 */

import type { ChangeDetail } from './types';

function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;

  if (typeof a === 'object' && typeof b === 'object') {
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch {
      return false;
    }
  }

  return false;
}

function getFieldType(value: unknown): string {
  if (value === null || value === undefined) return 'null';
  if (Array.isArray(value)) return 'array';
  if (value instanceof Date) return 'date';
  return typeof value;
}

/**
 * Builds a single ChangeDetail with the fieldType annotation derived from the
 * value — the same rule calculateAuditChanges applies. Use for hand-constructed
 * change entries (e.g. adoption/duplication flows) so they match the shape the
 * diff-based path produces.
 */
export function createChangeDetail(field: string, oldValue: unknown, newValue: unknown): ChangeDetail {
  return { field, oldValue, newValue, fieldType: getFieldType(newValue ?? oldValue) };
}

const SKIP_FIELDS = new Set([
  '_id',
  'createdAt',
  'updatedAt',
  'createdBy',
  'updatedBy',
  'deletedAt',
  'deletedBy',
  '_deleted',
  '_syncSeq',
  '__optimistic',
  'lock',
  'lockId',
  'lockOwner',
  'lockHeartbeatAt',
  'password',
  'passwordHash',
  'token',
  'refreshToken',
  'inviteToken',
  '_linkedEntityId',
  '_linkAction',
]);

/**
 * @process calculateAuditChanges
 *
 * Steps:
 * 1. Collect all unique keys from before and after objects.
 * 2. Skip system/sensitive fields defined in SKIP_FIELDS.
 * 3. Compare each field value; push a ChangeDetail for differences.
 *
 * Inputs:
 * - before: Record<string, unknown> | null — pre-mutation document state.
 * - after: Record<string, unknown> | null — post-mutation document state.
 * - options.arrayIdentityByField: optional host-provided array identities for
 *   keyed item diffs; arrays fall back to whole-field comparison if any item
 *   has no identity. A tombstoned `_deleted` item is represented as removal.
 *
 * Outputs:
 * - ChangeDetail[] — list of field-level changes (empty if either input is null).
 *
 * Failure Handling:
 * - Returns empty array if before or after is null/undefined.
 *
 * Idempotency: REQUIRED — same inputs always produce same output.
 */
export function calculateAuditChanges(
  before: Record<string, unknown> | null | undefined,
  after: Record<string, unknown> | null | undefined,
  options?: {
    arrayIdentityByField?: Readonly<Record<string, (item: unknown, index: number) => string | undefined>>;
  },
): ChangeDetail[] {
  if (!before || !after) return [];

  const changes: ChangeDetail[] = [];
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);

  for (const key of keys) {
    if (SKIP_FIELDS.has(key)) continue;

    const oldValue = before[key];
    const newValue = after[key];

    const identityForArray = options?.arrayIdentityByField?.[key];
    if (identityForArray && Array.isArray(oldValue) && Array.isArray(newValue)) {
      const keyedChanges = calculateKeyedArrayChanges(key, oldValue, newValue, identityForArray);
      if (keyedChanges) {
        changes.push(...keyedChanges);
        continue;
      }
    }

    if (!valuesEqual(oldValue, newValue)) {
      changes.push({
        field: key,
        oldValue: oldValue ?? null,
        newValue: newValue ?? null,
        fieldType: getFieldType(newValue ?? oldValue),
      });
    }
  }

  return changes;
}

function calculateKeyedArrayChanges(
  field: string,
  before: unknown[],
  after: unknown[],
  identityFor: (item: unknown, index: number) => string | undefined,
): ChangeDetail[] | null {
  const beforeById = new Map<string, unknown>();
  const afterById = new Map<string, unknown>();

  for (const [index, item] of before.entries()) {
    const identity = identityFor(item, index);
    if (identity === undefined) return null;
    beforeById.set(identity, item);
  }
  for (const [index, item] of after.entries()) {
    const identity = identityFor(item, index);
    if (identity === undefined) return null;
    afterById.set(identity, item);
  }

  const changes: ChangeDetail[] = [];
  const identities = new Set([...beforeById.keys(), ...afterById.keys()]);
  for (const identity of identities) {
    const oldItem = beforeById.get(identity);
    const newItem = afterById.get(identity);
    const oldIsActive = oldItem !== undefined && !isDeletedRecord(oldItem);
    const newIsActive = newItem !== undefined && !isDeletedRecord(newItem);
    const itemField = `${field}[${identity}]`;

    if (!oldIsActive && newIsActive) {
      changes.push(createChangeDetail(itemField, null, newItem));
      continue;
    }
    if (oldIsActive && !newIsActive) {
      changes.push(createChangeDetail(itemField, oldItem, null));
      continue;
    }
    if (!oldIsActive || !newIsActive || valuesEqual(oldItem, newItem)) continue;

    if (
      typeof oldItem === 'object'
      && oldItem !== null
      && typeof newItem === 'object'
      && newItem !== null
      && !Array.isArray(oldItem)
      && !Array.isArray(newItem)
    ) {
      changes.push(...calculateAuditChanges(
        oldItem as Record<string, unknown>,
        newItem as Record<string, unknown>,
      ).map((change) => ({ ...change, field: `${itemField}.${change.field}` })));
    } else {
      changes.push(createChangeDetail(itemField, oldItem, newItem));
    }
  }

  return changes;
}

function isDeletedRecord(value: unknown): boolean {
  return typeof value === 'object'
    && value !== null
    && !Array.isArray(value)
    && (value as Record<string, unknown>)._deleted === true;
}
